import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Color,
  CubeTexture,
  DoubleSide,
  Euler,
  FrontSide,
  Group,
  Intersection,
  Material,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Plane,
  Raycaster,
  Texture,
  Vector2,
  Vector3
} from "three";
import { CraneSideEnum, VesselPartTypeEnum } from "open-vessel-definition";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import {
  IBoundingBoxResult,
  computeBoundingBoxHelper,
  computeBoundingBoxMorphedHelper,
} from "../helpers/computeBoundingBoxHelper";
import {
  IVesselOnePart,
  IVesselOnePartBase,
  IVesselOnePartBridge,
  IVesselOnePartCrane,
  IVesselOnePartSmoke,
} from "../../types/IVesselOneParts";
import { cloneObject, roundDec } from "@tedivo/tedivo-pure-helpers";
import {
  computeMorphedAttributes,
  mergeGeometries,
} from "three/examples/jsm/utils/BufferGeometryUtils";
import { disposeObject3D, getMaterialTextures } from "../../../disposeObject3D";

import { Cache } from "../helpers/cache";
import { DecalGeometry } from "three/examples/jsm/geometries/DecalGeometry";
import { DivisorMaterial } from "../helpers/divisorMaterial";
import { Holder3D } from "../../types/holder3d";
import { IPartsChildren } from "../../types/IPartsConfigSchema";
import { IVesselOneParametrization } from "../../types/IVesselOneParametrization";
import { Module3D } from "./module3d";
import { VesselOneParameters } from "./parameters";
import { addParts } from "../helpers/partsProcessor/addParts";
import { applyMorphInfluences } from "../helpers/partsProcessor/applyMorphInfluences";
import { createGlobalGroupReferences } from "../helpers/partsProcessor/createGlobalGroupReferences";
import { getImplicitModulesNumber } from "../helpers/partsProcessor/getImplicitModulesNumber";
import { partsConfig } from "./partsConfig";
import { removeAndRenameParts } from "../helpers/partsProcessor/removeAndRenameParts";

export const MAX_WIDTH = 27,
  MIN_WIDTH = 4;

const CACHE_PREFIX = "_vesselModels";
const FLOOR_HEIGHT = 0.1;
const BAY_DEPTH = 0.47;
const BAY_DEPTH_SEP_X = 0.0061;

const PARTS_CONFIG = partsConfig(BAY_DEPTH, MAX_WIDTH, MIN_WIDTH);
const MODEL_FILENAME = "shipOneTedivo.glb?v=20240422";

export class VesselOne {
  static id = "vesselOne";
  static path = "/assets/vessels/" + MODEL_FILENAME;
  static _model3d: GLTF | undefined = undefined;
  static _parts: Record<string, Mesh | Object3D> = {};
  static _texturesByName: Record<string, Texture> = {};
  static _envMap: Texture | CubeTexture | null = null;

  private tallScale = 1;
  private addedModules = 0;

  private _deckMaterial: Material | undefined;
  private _separatorMaterial = new DivisorMaterial(false);
  private _scene3d = new Object3D();
  private _virtualModules3D: Module3D[] = [];
  private _groupReferences: Record<string, Mesh[]> = {};

  private _visible = true;
  private _showDecalMesh = false;

  private _fnsToExecWhenVisible: Record<string, () => void> = {};

  private _shellIntersectingPlane = new Plane(new Vector3(0, 0, 1), 0.01);
  private _showIntersectingPlane = false;
  private _materialsWithClippingPlanes: Material[] = [];

  public _components: {
    nose3D: Holder3D | undefined;
    bridge3D: Holder3D | undefined;
    tail3D: Holder3D | undefined;
    body: { modules3D: Holder3D; extras: Holder3D } | undefined;
    smokeStacks: Holder3D[];
    labels3D: Object3D | undefined;
  } = {
      nose3D: undefined,
      bridge3D: undefined,
      tail3D: undefined,
      body: undefined, //Holds Modules & Extras
      smokeStacks: [],
      labels3D: undefined,
    };

  private _parts: IVesselOnePart[] = [];
  private _partsByPk: Record<string, IVesselOnePart> = {};
  private bBoxInfo: IBoundingBoxResult | null = null;

  _params: IVesselOneParametrization = {
    maxCell: 0,
    minCell: 0,
    aboveDeck: 0,
    belowDeck: 0,
    propellers: 0,
    rudders: 0,
    separatorColor: "#ffffff",
    belowSeparatorColor: "#ffffff",
    visible: true,
  } as IVesselOneParametrization;

  private _deckVisible = true;
  private _shellVisible = true;
  private _singleGeometryMesh: Mesh | null = null;

  private __minMaxParams: { [s: string]: { min: number; max: number } } = {};
  private __tempCanvas: HTMLCanvasElement | null = null;
  private _onBuildReadyFn: (() => void) | null = null;

  public floorModuleBox: IBoundingBoxResult | null = null;

  constructor({
    envMap,
    onLoaded,
    autoLoad = true,
  }: {
    envMap: Texture | CubeTexture;
    onLoaded?: (vessel: VesselOne) => void;
    autoLoad?: boolean;
  }) {
    this._scene3d.name = "VesselOne";

    VesselOne._envMap = envMap;

    //Cache min/max
    VesselOneParameters.forEach((group) => {
      group.params.forEach((param) => {
        if (param.type === "selectRange") {
          this.__minMaxParams[param.id] = { min: param.min, max: param.max };
        }
      });
    });

    if (autoLoad) {
      VesselOne._load3DModelFile().then(() => {
        onLoaded?.(this);
      });
    }
  }

  get shellIntersectingPlane() {
    return this._shellIntersectingPlane;
  }
  set shellIntersectingPlane(p: Plane) {
    this._shellIntersectingPlane = p;
    if (this._showIntersectingPlane) {
      this._materialsWithClippingPlanes.forEach((m) => {
        m.clippingPlanes = [p];
        m.needsUpdate = true;
      });
    }
  }

  get showIntersectingPlane() {
    return this._showIntersectingPlane;
  }
  set showIntersectingPlane(v) {
    this._showIntersectingPlane = v;
    this._materialsWithClippingPlanes.forEach((m) => {
      m.clippingPlanes = v ? [this._shellIntersectingPlane] : [];
      m.needsUpdate = true;
    });
  }

  get visible() {
    return this._visible;
  }

  set visible(v) {
    v = !!v;
    this._visible = v;
    this._scene3d.visible = v;

    if (this._components.labels3D) {
      this._components.labels3D.visible = v && this.shellVisible;
    }

    if (v) {
      Object.keys(this._fnsToExecWhenVisible).forEach((fnName) => {
        this._fnsToExecWhenVisible[fnName]();
        delete this._fnsToExecWhenVisible[fnName];
      });
    }
  }

  get deckVisible() {
    return this._deckVisible;
  }

  set deckVisible(v) {
    const vs = !!v;

    this._deckVisible = vs;

    this._groupReferences["Deck"]?.forEach((o) => {
      o.visible = vs;
    });
  }

  get scene3d() {
    return this._scene3d;
  }

  get shellVisible() {
    return this._shellVisible;
  }

  set shellVisible(v) {
    const vs = !!v;
    this._shellVisible = vs;

    this._virtualModules3D.forEach((h) => {
      const bs = this._parts[h.bsPosition];
      const iv = bs.type !== VesselPartTypeEnum.BAY;
      const ob = h.get3D("Module-Lower");
      if (ob) {
        ob.traverse((o: Object3D) => {
          if (o instanceof Mesh) {
            o.visible = iv || vs;
            //o.material.side = vs ? FrontSide : DoubleSide;
          }
        });
      }
    });

    const modules3D = this._components.body?.modules3D.holder3D;
    if (!modules3D) return;

    const firstModule = modules3D.getObjectByName("Module-Lower") as
      | Mesh
      | undefined;

    if (firstModule) {
      firstModule.visible = vs;
    }

    setObjectVisibility(
      [
        "Tail-Obj",
        "Nose-Lower",
        "Anchor-Left",
        "Anchor-Right",
        this._components.labels3D,
      ],
      this._scene3d,
      vs,
    );

    //if (this._components.labels3D) this._components.labels3D.visible = vs;
  }

  get labelVisible() {
    return this._components.labels3D?.visible;
  }
  set labelVisible(v) {
    if (this._components.labels3D) {
      this._components.labels3D.visible = !!v;
    }
  }

  get showDecalMesh() {
    return this._showDecalMesh;
  }

  set showDecalMesh(v) {
    if (!this._singleGeometryMesh) {
      return;
    }

    const val = !!v;
    this._showDecalMesh = val;
    this._scene3d.visible = !val;

    setObjectVisibility(
      [this._singleGeometryMesh, this._components.labels3D],
      this._scene3d,
      val,
    );
  }

  get onBuildReady() {
    return this._onBuildReadyFn;
  }

  set onBuildReady(f) {
    if (f && typeof f === "function") {
      this._onBuildReadyFn = f;
    }
  }

  get singleGeometryMesh(): Mesh | null {
    return this._singleGeometryMesh;
  }

  get deckMaterial(): Material | undefined {
    return this._deckMaterial;
  }

  private execWhenVisible(
    name: string,
    fn: () => void,
    cbIfImmediate?: () => void,
  ) {
    if (this.visible) {
      Promise.resolve(fn()).then(() => {
        cbIfImmediate?.();
      });
    } else {
      this._fnsToExecWhenVisible[name] = fn;
      cbIfImmediate?.();
    }
  }

  computeVesselBoundingBox() {
    this.bBoxInfo = computeBoundingBoxHelper(this._scene3d);
  }

  addToScene(parent: Object3D) {
    parent.add(this._scene3d);
  }

  setParams(params: Partial<IVesselOneParametrization> = {}) {
    return new Promise<void>((resolve, reject) => {
      const prevParams = { ...this._params };

      (Object.keys(params) as (keyof IVesselOneParametrization)[]).forEach(
        (key) => {
          const minMaxPrm = this.__minMaxParams[key];

          if (!minMaxPrm) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this._params as any)[key] = params[key];
          } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this._params as any)[key] = Math.max(
              Math.min(params[key] as number, minMaxPrm.max),
              minMaxPrm.min,
            );
          }
        },
      );

      if (this.__build3DVessel()) {
        this.__changeTextureColors(prevParams);
        this.__hideObjects();
        this.__adjustScales();

        if (typeof params.visible === "boolean") {
          this.visible = params.visible;
        }

        this.execWhenVisible(
          "finish",
          async () => {
            await this.createSingleGeometry(this._scene3d);
            this.__addLabels();
            this._onBuildReadyFn?.();
            resolve();
          },
          () => {
            //resolve();
          },
        );
      } else {
        reject("There is an error building the 3D Vessel");
      }
    });
  }

  updateTextureColors(newParams: IVesselOneParametrization) {
    const prevParams = { ...this._params };
    this._params = { ...newParams };
    this.__changeTextureColors(prevParams);
  }

  static _load3DModelFile = (onProgress?: ((event: ProgressEvent<EventTarget>) => void)) => {
    const processModel = (model3d: GLTF): Promise<void> => {
      return new Promise((resolve) => {
        const envMap = this._envMap;

        model3d.scene.traverse((part: Object3D | Mesh) => {
          const hasMaterial = (part as Mesh).material as
            | MeshStandardMaterial
            | undefined;

          if (part.name) {
            VesselOne._parts[part.name] = part;
          }

          if (hasMaterial) {
            hasMaterial.envMap = envMap;
            hasMaterial.envMapIntensity = 0.75;
          }
        });

        (model3d.parser.getDependencies("texture") as Promise<Texture[]>).then(
          (textures) => {
            this._texturesByName = {};
            textures.forEach((texture) => {
              if (texture?.name) this._texturesByName[texture.name] = texture;
            });
            resolve();
          },
        );

        this._model3d = model3d;
      });
    };

    return new Promise((resolve, reject) => {
      const cacheName = `${CACHE_PREFIX}${this.id}`;
      const fromCache = Cache[cacheName];

      if (fromCache) {
        processModel(fromCache).then(() => {
          resolve(fromCache);
        });
        return;
      }

      const loader = new GLTFLoader();

      loader.load(
        this.path,
        (object) => {
          Cache[cacheName] = object;
          processModel(object).then(() => {
            resolve(object);
          });
        },
        onProgress,
        (err) => {
          console.error(err);
          reject(err);
        },
      );
    });
  };

  private __cleanScene() {
    //Clean the scene of Ship
    const sceneChildren = [...this._scene3d.children];
    const shared = VesselOne.__getSharedResources();

    for (let i = sceneChildren.length - 1; i >= 0; i--) {
      // Frees what this build created (labels, deck, cloned materials...), not the cached parts
      disposeObject3D(sceneChildren[i], shared);
      sceneChildren[i].removeFromParent();
    }

    this._materialsWithClippingPlanes = [];
  }

  /** Geometries, materials and textures cached across builds (the GLB parts and the divisors) */
  private static __getSharedResources(): Set<unknown> {
    const shared = new Set<unknown>();

    const addMaterial = (m: Material) => {
      shared.add(m);
      getMaterialTextures(m).forEach((t) => shared.add(t));
    };

    const addObject = (root: Object3D) =>
      root.traverse((obj) => {
        const { geometry, material } = obj as Partial<Mesh>;
        if (geometry) shared.add(geometry);
        if (material) (Array.isArray(material) ? material : [material]).forEach(addMaterial);
      });

    if (VesselOne._model3d) addObject(VesselOne._model3d.scene);
    Object.values(VesselOne._parts).forEach(addObject);
    Object.values(VesselOne._texturesByName).forEach((t) => shared.add(t));
    if (VesselOne._envMap) shared.add(VesselOne._envMap);
    Object.values(DivisorMaterial._materialsCache).forEach(addMaterial);
    Object.values(DivisorMaterial._texturesCache).forEach((t) => shared.add(t));

    return shared;
  }

  private __build3DVessel() {
    const maxCell = this._params.maxCell,
      minCell = this._params.minCell,
      rows = maxCell + (minCell === 1 ? 0 : 1),
      aboveDeck = this._params.aboveDeck,
      belowDeck = this._params.belowDeck,
      propellersAndRudders = `${this._params.propellers}P${this._params.rudders}R`,
      builkheads: Object3D[] = [],
      globalGroups: Record<string, string[]> = {};

    const alpha = (rows - MIN_WIDTH) / (MAX_WIDTH - MIN_WIDTH);
    const ADJUST_FACTOR = 0.1;

    const parts = cloneObject(this._params.parts || []);

    let TALL_SCALE = 1;

    //Initial estimation of containers below
    let belowSeparators =
      (alpha * 15.934126458115752 + (1 - alpha) * 2.947691172149334) * 0.75;

    if (belowDeck > belowSeparators) {
      TALL_SCALE = belowDeck / belowSeparators;
      belowSeparators = belowDeck;
    }

    if (!rows) {
      return false;
    }

    this.tallScale = TALL_SCALE;

    //Let's calculate depth of Ship
    const addedModules = getImplicitModulesNumber(
      PARTS_CONFIG["Tail"].extraModules,
      rows,
    );

    this.addedModules = addedModules;

    // ATTENTION: Separate modules are those that are not part of the Tail
    const partsInModule = parts.slice();
    const partsInTail = partsInModule.splice(-addedModules); // Remove the last addedModules from partsInModule

    this._parts = parts;

    //Holders
    const nose3D = new Holder3D("Nose"),
      tail3D = new Holder3D("Tail"),
      virtualModules3D: Module3D[] = [];

    const modules3D = new Holder3D("Modules"),
      extras = new Holder3D("Extras");

    this._components.nose3D = nose3D;
    this._components.tail3D = tail3D;

    this._components.body = {
      modules3D,
      extras,
    };

    const separatorMaterial = this._separatorMaterial;
    separatorMaterial.color = new Color(this._params.separatorColor);
    separatorMaterial.belowColor = new Color(this._params.belowSeparatorColor);

    const buildNose = () => {
      const { partsGroups } = addParts({
        conf: PARTS_CONFIG["Nose"],
        holder3D: nose3D,
        cells: rows,
        partsLibrary: VesselOne._parts,
        MAX_WIDTH,
        MIN_WIDTH,
      });

      Object.assign(globalGroups, partsGroups);

      _getObjectByName(nose3D.holder3D, "Nose-Lower", (obj) => {
        obj.scale.y = TALL_SCALE;
      });

      this._scene3d.add(nose3D.holder3D);
    };

    const buildModules = () => {
      this._scene3d.add(modules3D.holder3D);
      this._scene3d.add(extras.holder3D);

      //Create modules
      const totalModulesLength = partsInTail.reduce((acc, bs) => {
        return Math.min(acc, bs.positionX);
      }, Infinity);

      ["ModuleBody", "ModuleSeparators"].forEach((configKey) => {
        const { partsGroups } = addParts({
          conf: PARTS_CONFIG[configKey],
          holder3D: modules3D,
          cells: rows,
          partsLibrary: VesselOne._parts,
          MAX_WIDTH,
          MIN_WIDTH,
        });

        Object.assign(globalGroups, partsGroups);

        const lenRatio = totalModulesLength / BAY_DEPTH;
        modules3D.get3D("Module-Lower").scale.x = lenRatio;
        modules3D.get3D("Module-Floor").scale.x = lenRatio;
        //modules3D.get3D("Module-Floor").visible = false;

        modules3D.positionX = 0;

        _getObjectByName(modules3D.holder3D, "Module-Lower", (obj) => {
          obj.scale.y = TALL_SCALE;

          const mat = ((obj as Mesh).material as Material).clone();
          mat.clippingPlanes = [this._shellIntersectingPlane];
          mat.needsUpdate = true;
          this._materialsWithClippingPlanes.push(mat);
          (obj as Mesh).material = mat;
        });

        _getObjectByName(
          modules3D.holder3D,
          "Module-Separator-Lower",
          (obj) => {
            obj.scale.y = TALL_SCALE;
          },
        );

        const moduleFloor = modules3D.get3D("Module-Floor") as Mesh | undefined;
        if (moduleFloor) {
          const bbx = computeBoundingBoxMorphedHelper(moduleFloor);
          this.floorModuleBox = bbx;
          this._deckMaterial = moduleFloor.material as Material;
        }

        // Lashing bridges are not part of the modules: see createLashingBridgesGroup
        //TODO: Add separators textures
      });
    };

    const buildBridges = () => {
      const bridges = parts.filter(
        (bs) => bs.type === VesselPartTypeEnum.BRIDGE,
      ) as IVesselOnePartBridge[];

      const extras = this._components.body?.extras;
      if (!extras) return;

      const topPos = aboveDeck * FLOOR_HEIGHT + 0.07;

      bridges.forEach((bridgeInfo) => {
        const bridge3D = new Holder3D("Bridge3D");
        extras.add3D(bridgeInfo.label, bridge3D);

        const { measures, partsGroups } = addParts({
          conf: PARTS_CONFIG["BridgeAbove"],
          holder3D: bridge3D,
          cells: rows,
          partsLibrary: VesselOne._parts,
          MAX_WIDTH,
          MIN_WIDTH,
        });

        Object.assign(globalGroups, partsGroups);

        //-Re-position
        bridge3D.get3D("Bridge-last-floor").position.y =
          aboveDeck * FLOOR_HEIGHT;
        //-Clone nth floors
        const floor = bridge3D.get3D("Bridge-nth-floor") as Mesh;

        if (aboveDeck < 2) {
          floor.visible = false;
        } else {
          floor.position.y = FLOOR_HEIGHT;
          for (let ad = 2; ad < aboveDeck; ad += 1) {
            const floorName = "Bridge-nth-floor-" + ad;
            const nextFloor = floor.clone?.();
            bridge3D.add3D(floorName, nextFloor);
            nextFloor.position.y = ad * FLOOR_HEIGHT;
          }
        }

        if (!bridgeInfo.heatSrcBelow) {
          bridge3D.setChildVisible("Bridge-Smoke-Lower", false);
          bridge3D.setChildVisible("Bridge-Smoke-Upper", false);
        } else {
          const top = bridge3D.get3D("Bridge-Smoke-Upper");
          top.position.y = topPos;
          top.position.x = 0.14;

          const bottom = bridge3D.get3D("Bridge-Smoke-Lower");
          bottom.scale.y = topPos / measures[bottom.uuid].size.y;
          bottom.position.x = 0.14;
        }

        putInPosition({
          hl: bridge3D,
          info: bridgeInfo,
          autoScale: true,
          autoCenter: false,
        });
      });
    };

    const buildSmokes = () => {
      const flipBufferGeometryNormals = (model: Mesh) => {
        const geometry = model.geometry.clone();
        const tempXYZ = [0, 0, 0];
        geometry.scale(1, 1, -1);

        // flip normals
        if (geometry.attributes["normal"]) {
          for (
            let i = 0;
            i < geometry.attributes["normal"].array.length / 9;
            i++
          ) {
            // cache a coordinates
            tempXYZ[0] = geometry.attributes["normal"].array[i * 9];
            tempXYZ[1] = geometry.attributes["normal"].array[i * 9 + 1];
            tempXYZ[2] = geometry.attributes["normal"].array[i * 9 + 2];

            // overwrite a with c
            geometry.attributes["normal"].array[i * 9] =
              geometry.attributes["normal"].array[i * 9 + 6];
            geometry.attributes["normal"].array[i * 9 + 1] =
              geometry.attributes["normal"].array[i * 9 + 7];
            geometry.attributes["normal"].array[i * 9 + 2] =
              geometry.attributes["normal"].array[i * 9 + 8];

            // overwrite c with stored a values
            geometry.attributes["normal"].array[i * 9 + 6] = tempXYZ[0];
            geometry.attributes["normal"].array[i * 9 + 7] = tempXYZ[1];
            geometry.attributes["normal"].array[i * 9 + 8] = tempXYZ[2];
          }
        }

        // change face winding order
        for (
          let i = 0;
          i < geometry.attributes["position"].array.length / 9;
          i++
        ) {
          // cache a coordinates
          tempXYZ[0] = geometry.attributes["position"].array[i * 9];
          tempXYZ[1] = geometry.attributes["position"].array[i * 9 + 1];
          tempXYZ[2] = geometry.attributes["position"].array[i * 9 + 2];

          // overwrite a with c
          geometry.attributes["position"].array[i * 9] =
            geometry.attributes["position"].array[i * 9 + 6];
          geometry.attributes["position"].array[i * 9 + 1] =
            geometry.attributes["position"].array[i * 9 + 7];
          geometry.attributes["position"].array[i * 9 + 2] =
            geometry.attributes["position"].array[i * 9 + 8];

          // overwrite c with stored a values
          geometry.attributes["position"].array[i * 9 + 6] = tempXYZ[0];
          geometry.attributes["position"].array[i * 9 + 7] = tempXYZ[1];
          geometry.attributes["position"].array[i * 9 + 8] = tempXYZ[2];
        }

        // flip UV coordinates
        for (let i = 0; i < geometry.attributes["uv"].array.length / 6; i++) {
          // cache a coordinates
          tempXYZ[0] = geometry.attributes["uv"].array[i * 6];
          tempXYZ[1] = geometry.attributes["uv"].array[i * 6 + 1];

          // overwrite a with c
          geometry.attributes["uv"].array[i * 6] =
            geometry.attributes["uv"].array[i * 6 + 4];
          geometry.attributes["uv"].array[i * 6 + 1] =
            geometry.attributes["uv"].array[i * 6 + 5];

          // overwrite c with stored a values
          geometry.attributes["uv"].array[i * 6 + 4] = tempXYZ[0];
          geometry.attributes["uv"].array[i * 6 + 5] = tempXYZ[1];
        }

        if (geometry.attributes["normal"]) {
          geometry.attributes["normal"].needsUpdate = true;
        }
        geometry.attributes["position"].needsUpdate = true;
        geometry.attributes["uv"].needsUpdate = true;

        return new Mesh(geometry, model.material);
      };

      const buildSingleSmoke = (
        conf: IPartsChildren,
        hldr3D: Holder3D,
        isDouble: boolean,
      ) => {
        const topPos = aboveDeck * FLOOR_HEIGHT,
          sidePos = Math.min((rows / 2) * 0.05, 0.3);

        const { measures, partsGroups } = addParts({
          conf,
          holder3D: hldr3D,
          cells: rows,
          partsLibrary: VesselOne._parts,
          MAX_WIDTH,
          MIN_WIDTH,
        });

        Object.assign(globalGroups, partsGroups);

        if (isDouble) {
          const top = hldr3D.get3D("Smoke-Funnel-Upper-Single-14-28");
          top.position.y = topPos;
          top.position.z = sidePos;

          const bottom = hldr3D.get3D("Smoke-Funnel-Lower-Single-14-28");
          const bottomHeight = measures[bottom.uuid].size.y;
          bottom.scale.y = topPos / bottomHeight;
          bottom.position.z = sidePos;

          const top2 = flipBufferGeometryNormals(top as Mesh);
          top2.position.y = topPos;
          top2.position.z = -sidePos;
          hldr3D.add3D("top-2", top2);

          const bottom2 = flipBufferGeometryNormals(bottom as Mesh);
          bottom2.scale.y = topPos / bottomHeight;
          bottom2.position.z = -sidePos;
          hldr3D.add3D("bottom-2", bottom2);
        } else {
          const top = hldr3D.get3D("Smoke-Funnel-Upper-Single-14-28");
          top.position.y = topPos;

          const bottom = hldr3D.get3D("Smoke-Funnel-Lower-Single-14-28");
          bottom.scale.y = topPos / measures[bottom.uuid].size.y;
        }
      };

      const conf = PARTS_CONFIG["Smoke"];
      const extras = this._components.body?.extras;
      if (!extras) return;

      const smokes = parts.filter(
        (bs) => bs.type === VesselPartTypeEnum.SMOKE,
      ) as IVesselOnePartSmoke[];

      smokes.forEach((sk) => {
        const smoke3D = new Holder3D("Smoke3D");
        extras.add3D(sk.label, smoke3D);

        const isDouble = sk.numberOfSmokeStacks > 1;

        buildSingleSmoke(conf, smoke3D, !!isDouble);
        putInPosition({
          hl: smoke3D,
          info: sk,
          autoScale: true,
          autoCenter: false,
        });
      });
    };

    const buildCranes = () => {
      const buildSingleCrane = (
        conf: IPartsChildren,
        crane3D: Holder3D,
        side: CraneSideEnum,
      ) => {
        const { measures, partsGroups } = addParts({
          conf,
          holder3D: crane3D,
          cells: rows,
          partsLibrary: VesselOne._parts,
          MAX_WIDTH,
          MIN_WIDTH,
        });

        Object.assign(globalGroups, partsGroups);

        const rot =
          side === CraneSideEnum.CENTER
            ? 0
            : (side === CraneSideEnum.PORT ? Math.PI * 0.5 : -Math.PI * 0.5) -
            0.05;

        const posZ =
          side === CraneSideEnum.CENTER
            ? 0
            : side === CraneSideEnum.PORT
              ? rows * 0.035
              : rows * -0.035;

        //-Re-position top
        const top = crane3D.get3D("Crane-Top");
        const pos = aboveDeck * FLOOR_HEIGHT;
        top.position.y = pos;
        top.rotation.y = rot;
        top.position.z = posZ;
        //-Re-scale
        const floor = crane3D.get3D("Crane-Base") as Mesh;
        floor.scale.y = pos / measures[floor.uuid].size.y;
        floor.position.z = posZ;

        const step1 = crane3D.get3D("Crane-Stair-Module") as Mesh,
          step1Height = measures[step1.uuid].size.y,
          repets = Math.ceil(pos / step1Height);

        step1.visible = false;
        const step1PosZ = measures[step1.uuid].size.x;

        for (let r = 1; r < repets; r += 1) {
          const stepN = step1.clone();
          stepN.visible = true;
          stepN.material = floor.material;
          stepN.position.z = posZ - step1PosZ;
          stepN.position.y = r * step1Height;
        }
      };

      const extras = this._components.body?.extras;
      if (!extras) return;

      const cranes = parts.filter(
        (bs) => bs.type === VesselPartTypeEnum.CRANE,
      ) as IVesselOnePartCrane[];

      cranes.forEach((sk) => {
        const crane3D = new Holder3D("Crane3D");
        extras.add3D("", crane3D);

        sk.cranes.forEach((side) => {
          buildSingleCrane(PARTS_CONFIG["Crane"], crane3D, side);
        });

        putInPosition({
          hl: crane3D,
          info: sk,
          autoScale: false,
          autoCenter: true,
          partRefName: "Crane-Base",
        });
      });
    };

    const buildTail = () => {
      const { partsGroups } = addParts({
        conf: PARTS_CONFIG["Tail"],
        holder3D: tail3D,
        cells: rows,
        partsLibrary: VesselOne._parts,
        MAX_WIDTH,
        MIN_WIDTH,
      });

      Object.assign(globalGroups, partsGroups);

      removeAndRenameParts(PARTS_CONFIG["Tail"], tail3D, rows);

      const tailGroupRefs = createGlobalGroupReferences(
        tail3D.holder3D,
        partsGroups,
      );

      // Remove propellers and rudders
      ["1P1R", "1P2R", "2P1R", "2P2R"].forEach((prGroup) => {
        tailGroupRefs[prGroup].forEach((g3D) => {
          if (prGroup !== propellersAndRudders) {
            g3D.removeFromParent();
          }
        });
      });

      //Adjust scale
      const tailObh = tail3D.getObjectByName("Tail-Obj");
      if (tailObh) {
        tailObh.scale.y = TALL_SCALE;

        const mat = ((tailObh as Mesh).material as Material).clone();
        mat.clippingPlanes = [this._shellIntersectingPlane];
        mat.needsUpdate = true;
        this._materialsWithClippingPlanes.push(mat);
        (tailObh as Mesh).material = mat;
      }

      const tail3DHolder = tail3D.holder3D;
      tail3DHolder.name = "Tail-Holder";
      this._scene3d.add(tail3DHolder);

      tail3D.positionX = parts[partsInModule.length].positionX || 0;

      for (let j = 0; j < addedModules; j += 1) {
        const bsPos = partsInModule.length + j;
        const bsPart = parts[bsPos];
        const holder3D = _getObjectByName(tail3DHolder, "Tail-Module-" + j);

        //Add to Virtual Modules
        if (holder3D) {
          const mod3D = new Module3D(bsPart.label, holder3D, bsPos, 1);
          virtualModules3D[bsPos] = mod3D;
        }

        // AFT bulkhead
        if (bsPart.type === VesselPartTypeEnum.BAY) {
          const sepLower = virtualModules3D[bsPos]?.get3D(
            "Module-Separator-Lower",
          );
          if (sepLower) sepLower.visible = bsPart.bulkheads?.aft !== undefined;
          if (sepLower?.visible) builkheads.push(sepLower);
        }

        // FW bulkhead
        const bsPartPrev = parts[bsPos - 1];
        if (bsPartPrev && bsPartPrev.type === VesselPartTypeEnum.BAY) {
          const mod3DPrev = virtualModules3D[bsPos - 1];
          const sepLowerPrev = mod3DPrev?.get3D("Module-Separator-Upper");
          if (sepLowerPrev)
            sepLowerPrev.visible = bsPartPrev.bulkheads?.fore !== undefined;
          if (sepLowerPrev?.visible) builkheads.push(sepLowerPrev);
        }
      }

      //Hack: Reduce hatch-cover size at end in 4 (bug in 3D model)
      if (rows === 4) {
        const lastTailMod3D = _getObjectByName(
          tail3DHolder,
          "Tail-Module-" + (addedModules - 1),
        );

        if (lastTailMod3D) {
          _getObjectByName(lastTailMod3D, "Module-Floor", (mf) => {
            mf.scale.x = mf.scale.x * 0.99;
          });

          _getObjectByName(lastTailMod3D, "Module-Separator-Upper", (us0) => {
            us0.position.x -= BAY_DEPTH_SEP_X;
          });

          _getObjectByName(
            lastTailMod3D,
            "Module-Separator-Upper-Side",
            (us1) => {
              us1.position.x -= BAY_DEPTH_SEP_X;
            },
          );

          _getObjectByName(
            lastTailMod3D,
            "Module-Separator-Upper-Diagonal",
            (us2) => {
              us2.position.x -= BAY_DEPTH_SEP_X;
            },
          );
        }
      }

      //Hack: Reduce tail (due to proportions in View) for 4-11
      if (rows <= 11) {
        const p = (rows - 4) / 7;

        _getObjectByName(tail3DHolder, "Tail-Obj", (tailObj) => {
          tailObj.scale.x = p * 0.62 + (1 - p) * 1;
        });

        _getObjectByName(tail3DHolder, "Tail-Module-0", (tailObj) => {
          _getObjectByName(tailObj, "Module-Separator-Lower", (o) => {
            o.visible = false;
          });
        });
      }

      //Morph geometries
      applyMorphInfluences(PARTS_CONFIG["Tail"], tail3D, rows);
    };

    const buildBulkheads = () => {
      const bh3D = _getObjectByName(
        modules3D.holder3D,
        "Module-Separator-Lower",
      );

      if (!bh3D) return;
      bh3D.visible = false;

      parts.forEach((bs, idx) => {
        if (bs.type === VesselPartTypeEnum.BAY && bs.bulkheads) {
          if (bs.bulkheads.aft) {
            const x = parts[idx + 1]?.positionX;
            if (x !== undefined) {
              const aft = bh3D.clone();
              aft.position.set(x - 0.04, bh3D.position.y, bh3D.position.z);
              aft.visible = true;
              modules3D.holder3D.add(aft);
              builkheads.push(aft);
            }
          }

          if (bs.bulkheads.fore) {
            const fore = bh3D.clone();
            const x = bs.positionX;
            if (x !== undefined) {
              fore.position.set(x - 0.04, bh3D.position.y, bh3D.position.z);
              fore.visible = true;
              modules3D.holder3D.add(fore);
              builkheads.push(fore);
            }
          }
        }
      });
    };

    const applyChangesToVirtualModules = () => {
      let holder3D;

      virtualModules3D.forEach((vm) => {
        const holder3D = vm.holder3DInstance;

        //Apply materials to separators
        separatorMaterial.applyToCombo(
          _getObjectByName(holder3D, "Module-Separator-Upper") as
          | Mesh
          | undefined,
          _getObjectByName(holder3D, "Module-Separator-Upper-Diagonal") as
          | Mesh
          | undefined,
          rows,
          0,
        );

        separatorMaterial.applyToCombo(
          _getObjectByName(holder3D, "Module-Separator-Lower") as
          | Mesh
          | undefined,
          undefined,
          rows,
          0,
        );
      });

      builkheads.forEach((bh) => {
        separatorMaterial.applyToCombo(
          _getObjectByName(bh, "Module-Separator-Lower") as Mesh | undefined,
          undefined,
          rows,
          0,
        );
      });

      _getObjectByName(holder3D, "Module-Separator-Upper-Side", (s) => {
        separatorMaterial.colorizeSide(
          (s as Mesh).material as MeshStandardMaterial,
          this._params.separatorColor,
        );
      });
    };

    this.__cleanScene();

    buildNose();
    buildModules();
    buildTail();
    buildBridges();
    buildSmokes();
    buildCranes();
    buildBulkheads();

    applyChangesToVirtualModules();

    this._virtualModules3D = virtualModules3D;
    this._groupReferences = createGlobalGroupReferences(
      this._scene3d,
      globalGroups,
    );

    return true;

    function putInPosition({
      hl,
      info,
      autoScale,
      autoCenter,
      partRefName,
    }: {
      hl: Holder3D;
      info: IVesselOnePartBase;
      autoScale: boolean;
      autoCenter: boolean;
      partRefName?: string | false;
    }): IBoundingBoxResult | null {
      if (partRefName !== false) hl.recenterX(partRefName);

      const len = info.len / 100;
      let boxFF: IBoundingBoxResult | null = null;

      if (autoScale) {
        boxFF = computeBoundingBoxHelper(hl.holder3D);
        const scaleX = boxFF?.size.x ? len / boxFF.size.x : 1;
        hl.holder3D.scale.x = scaleX * (1 - ADJUST_FACTOR);
      }

      if (info.positionX !== undefined) {
        const adj = autoCenter
          ? len * (1 - ADJUST_FACTOR) * 0.5
          : len * ADJUST_FACTOR * 0.5;

        const x = info.positionX + adj;
        hl.holder3D.position.x = x;
      }

      return boxFF;
    }
  }

  private __hideObjects() {
    const params = this._params;

    //Hide deck?
    this.deckVisible = params.deckVisible;

    //Hide Shell?
    this.shellVisible = params.shellVisible;

    // //Hide upper separators
    // virtualModules3D
    //   .filter((vm) => parts[vm.bsPosition].hideSepAbove)
    //   .forEach((vm) => {
    //     const vmh = vm.holder3DInstance;
    //     _getObjectByName(vmh, "Module-Separator-Upper", hideObj);
    //     _getObjectByName(vmh, "Module-Separator-Upper-Diagonal", hideObj);
    //     _getObjectByName(vmh, "Module-Separator-Upper-Side", hideObj);
    //   });

    // //Hide lower separators
    // virtualModules3D
    //   .filter(
    //     (vm) =>
    //       !params.belowSeparatorsVisible ||
    //       parts[vm.bsPosition].hideSepBelow,
    //   )
    //   .forEach((vm) => {
    //     const vmh = vm.holder3DInstance;
    //     _getObjectByName(vmh, "Module-Separator-Lower", hideObj);

    //     // _getObjectByName(vmh, "MSLT-0-4-11", hideObj);
    //     // _getObjectByName(vmh, "MSLT-0-4-11-Bottom", hideObj);
    //     // _getObjectByName(vmh, "MSLT-0-12-19", hideObj);
    //     // _getObjectByName(vmh, "MSLT-0-12-19-Bottom", hideObj);
    //     // _getObjectByName(vmh, "MSLT-0-20-27", hideObj);
    //     // _getObjectByName(vmh, "MSLT-0-20-27-Bottom", hideObj);
    //     // _getObjectByName(vmh, "MSLT-1-12-19", hideObj);
    //     // _getObjectByName(vmh, "MSLT-1-12-19-Bottom", hideObj);
    //     // _getObjectByName(vmh, "MSLT-1-20-27", hideObj);
    //     // _getObjectByName(vmh, "MSLT-1-20-27-Bottom", hideObj);
    //     // _getObjectByName(vmh, "MSLT-2-20-27", hideObj);
    //     // _getObjectByName(vmh, "MSLT-2-20-27-Bottom", hideObj);
    //   });
  }

  private __changeTextureColors(
    prevParams: Partial<IVesselOneParametrization> = {},
  ) {
    const internalColorSettings: Array<{
      name: keyof Pick<
        IVesselOneParametrization,
        | "mainColor"
        | "bottomColor"
        | "cranesColor"
        | "smokeBottomColor"
        | "smokeTopColor"
        | "bridgeColor"
      >;
      x: number;
      y: number;
      w: number;
      h: number;
    }> = [
        { name: "mainColor", x: 0, y: 0, w: 128, h: 64 },
        { name: "bottomColor", x: 0, y: 64, w: 64, h: 64 },
        { name: "cranesColor", x: 0, y: 192, w: 64, h: 64 },
        { name: "smokeBottomColor", x: 64, y: 192, w: 64, h: 64 },
        { name: "smokeTopColor", x: 128, y: 192, w: 64, h: 64 },
        { name: "bridgeColor", x: 320, y: 448, w: 64, h: 64 },
      ];

    let hasChanged = false;

    for (let i = 0; i < internalColorSettings.length; i += 1) {
      const colorName = internalColorSettings[i].name;
      if (prevParams[colorName] !== this._params[colorName]) {
        hasChanged = true;
        break;
      }
    }

    if (!hasChanged) {
      return;
    }

    const texture = VesselOne._texturesByName["ship_color_map.png"];

    if (!texture) {
      return;
    }

    let canvas = this.__tempCanvas;
    if (!canvas) {
      canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      this.__tempCanvas = canvas;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    //Main color
    ctx.drawImage(texture.image as CanvasImageSource, 0, 0);
    internalColorSettings.forEach((v) => {
      ctx.fillStyle = this._params[v.name];
      ctx.fillRect(v.x, v.y, v.w, v.h);
    });

    //Back color
    ctx.fillStyle = "#eeeeee";
    ctx.fillRect(64, 64, 64, 64);

    texture.image = canvas;
    texture.needsUpdate = true;
  }

  private __adjustScales() {
    //0. Compute bounding Boxes
    this._scene3d.traverse((node) => {
      if (node instanceof Mesh && node.geometry) {
        node.geometry.computeBoundingBox();
      }
    });

    //1. Adjust Above separators height
    //TODO: Use per bay slot separatos
    const aboveSeparators = 0; //this._params.aboveSeparators;

    this._groupReferences["Separators-Upper"].forEach((obj3D) => {
      if (aboveSeparators) {
        if (obj3D instanceof Mesh) {
          if (!obj3D.geometry.boundingBox) obj3D.geometry.computeBoundingBox();

          const boundingBox = obj3D.geometry.boundingBox;
          if (!boundingBox) return;

          const cY = boundingBox.max.y - boundingBox.min.y;

          obj3D.scale.y = (0.1 * aboveSeparators) / cY;
        }
      } else {
        obj3D.visible = false;
      }
    });
  }

  private __addLabels() {
    const OUT_VEC = 300,
      Y_PER_BELOW = 0.47;

    const parts = this._parts,
      virtualModules3D = this._virtualModules3D,
      params = this._params,
      labelsInfo: (ILabelInfo | null)[] = [],
      raycaster = new Raycaster();

    const labels3D = new Group();
    labels3D.name = "Labels-3D";
    this._scene3d.add(labels3D);
    this._components.labels3D = labels3D;

    const cells = this._params.maxCell + (this._params.minCell === 1 ? 0 : 1);
    const morphConstant = (cells - MIN_WIDTH) / (MAX_WIDTH - MIN_WIDTH);

    /** Raycast to intersect objects
     * @param obj3D Object3D to intersect
     * @param label Label to add
     * @param v3 Vector3 with position
     * @param dir Direction to intersect
     * @returns Object with intersection, orientation and startBoardSide
     * */
    function _findPosAndVectorOnShell({
      obj3D,
      label,
      fromV3,
      dir,
    }: {
      obj3D: Object3D;
      label: string;
      fromV3: Vector3;
      dir?: Vector3;
    }): ILabelInfo | null {
      const dir3 = new Vector3();
      const intersects: Intersection<Object3D>[] = [];

      if (!dir) {
        // Raycast from sides
        dir3.z = fromV3.z > 0 ? -1 : 1;
      } else {
        dir3.copy(dir);
      }

      const direction = dir3.clone().normalize();

      raycaster.set(fromV3, direction);
      raycaster.far = OUT_VEC;
      raycaster.intersectObject(obj3D, false, intersects);

      const intersection: Intersection<Object3D> | null = intersects[0];

      if (!intersection || !intersection.face) {
        return null;
      }

      // Correct! 0 is port, 1 is stbd
      const startBoardSide: 0 | 1 = fromV3.z < 0 ? 1 : 0;

      const orientation = new Euler().setFromVector3(
        new Vector3(0, startBoardSide ? Math.PI / 2 : -Math.PI / 2, 0),
        "XYZ",
      );

      //addSphere(label, intersection.point, intersection.face.normal);

      return {
        label,
        intersection,
        orientation,
        startBoardSide,
      };
    }

    const addBayLabels = (singleGeometryMesh: Mesh) => {
      // const labelsColor = new Color(params.labelsBaysColor);

      const tailObj3D = this._scene3d.getObjectByName("Tail-Obj");

      let prevYval = 0,
        yVal;

      function createBayLabelDecal({ label, intersection }: ILabelInfo) {
        const size = new Vector3(0.075, 0.075, 0.01),
          pos = intersection && intersection.point;

        if (
          !singleGeometryMesh ||
          !pos ||
          !intersection?.object ||
          isNaN(pos.x) ||
          isNaN(pos.y) ||
          isNaN(pos.z)
        ) {
          return;
        }

        const texture = createLabelTexture({
          text: label || "??????",
          width: 64,
          height: 64,
          fontSize: 40,
          flipY: true,
          bold: false,
        });

        const orientationE = new Euler();
        orientationE.y = (pos.z < 0 ? -1 : 0) * Math.PI;

        const geom = new DecalGeometry(
          singleGeometryMesh as Mesh,
          new Vector3(pos.x - size.x * 0.5, pos.y, pos.z),
          orientationE,
          size,
        );

        const material = new MeshStandardMaterial({
          color: 0x000000,
          map: texture,
          normalScale: new Vector2(1, 1),
          transparent: true,
          depthTest: true,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -4,
          wireframe: false,
          side: FrontSide,
          envMap: VesselOne._envMap,
          envMapIntensity: 0.95,
        });

        const mesh = new Mesh(geom, material);

        material.needsUpdate = true;
        mesh.name = label;

        labels3D.add(mesh);
      }

      function createLabelInfoForTail(mod3: Module3D) {
        const pos = new Vector3(),
          mL = mod3.get3D("Module-Lower") || tailObj3D,
          mSL = mod3.get3D("Module-Separator-Lower"),
          bs = parts[mod3.bsPosition];

        let bboxML = null,
          bboxMSL = null,
          firstYval = 0;

        if (bs.type === VesselPartTypeEnum.BAY && mL) {
          bboxML = computeBoundingBoxHelper(mL);
          bboxMSL = mSL ? computeBoundingBoxHelper(mSL) : undefined;

          mod3.holder3DInstance.getWorldPosition(pos);
          mod3.worldPosition = {
            pos,
            bbox: bboxML || undefined,
          };

          yVal = bboxMSL ? bboxMSL.size.y * 1.1 : prevYval;
          if (firstYval === 0) firstYval = yVal;

          if (prevYval === 0) {
            prevYval = yVal;
          } //Average (smooth y change)

          const mlObj3D = mod3.get3D("Module-Lower") || tailObj3D;
          if (!mlObj3D) return firstYval;

          addLabel3DInfo(bs, mlObj3D, pos, yVal);

          prevYval = yVal;
          return firstYval;
        }

        return firstYval;
      }

      function addLabel3DInfo(
        bs: IVesselOnePart,
        mlObj3D: Object3D,
        pos: Vector3,
        yVal: number,
        goUp = true,
      ) {
        if (bs.len < 40) {
          //PORT
          labelsInfo.push(
            _findPosAndVectorOnShell({
              obj3D: mlObj3D,
              label: bs.label,
              fromV3: new Vector3(
                pos.x + BAY_DEPTH / 4,
                -yVal * Y_PER_BELOW + 0.2,
                OUT_VEC,
              ),
            }),
          );
          //STBD
          labelsInfo.push(
            _findPosAndVectorOnShell({
              obj3D: mlObj3D,
              label: bs.label,
              fromV3: new Vector3(
                pos.x + BAY_DEPTH / 4,
                -yVal * Y_PER_BELOW,
                -OUT_VEC,
              ),
            }),
          );
        } else {
          const labels = bs.label.split("-");
          //PORT First 20
          labelsInfo.push(
            _findPosAndVectorOnShell({
              obj3D: mlObj3D,
              label: labels[0],
              fromV3: new Vector3(
                pos.x + BAY_DEPTH / 4,
                goUp
                  ? (-(prevYval + yVal) / 2) * Y_PER_BELOW
                  : -yVal * Y_PER_BELOW,
                OUT_VEC,
              ),
            }),
          );
          //STBD First 20
          labelsInfo.push(
            _findPosAndVectorOnShell({
              obj3D: mlObj3D,
              label: labels[0],
              fromV3: new Vector3(
                pos.x + BAY_DEPTH / 4,
                goUp
                  ? (-(prevYval + yVal) / 2) * Y_PER_BELOW
                  : -yVal * Y_PER_BELOW,
                -OUT_VEC,
              ),
            }),
          );
          //PORT Second 20
          labelsInfo.push(
            _findPosAndVectorOnShell({
              obj3D: mlObj3D,
              label: labels[1],
              fromV3: new Vector3(
                pos.x + (BAY_DEPTH * 3) / 4,
                -yVal * Y_PER_BELOW,
                OUT_VEC,
              ),
            }),
          );
          //STBD Second 20
          labelsInfo.push(
            _findPosAndVectorOnShell({
              obj3D: mlObj3D,
              label: labels[1],
              fromV3: new Vector3(
                pos.x + (BAY_DEPTH * 3) / 4,
                -yVal * Y_PER_BELOW,
                -OUT_VEC,
              ),
            }),
          );
        }
      }

      this.shellVisible = true;

      // Add labels to tail
      const labelY = virtualModules3D
        .filter(Boolean)
        .map(createLabelInfoForTail)[0];

      // Add labels to bays
      const partsInModule = parts.slice();
      partsInModule.splice(-this.addedModules); // Remove the last addedModules from partsInModule
      partsInModule.forEach((bs) => {
        if (bs.type === VesselPartTypeEnum.BAY) {
          const pos = new Vector3(bs.positionX, 0, 0);
          addLabel3DInfo(bs, singleGeometryMesh, pos, labelY || 0, false);
        }
      });

      labelsInfo.filter(Boolean).forEach((labelInfo) => {
        createBayLabelDecal(labelInfo as ILabelInfo);
      });
    };

    const addBrandNose = (singleGeometryMesh: Mesh) => {
      if (!params.shellVisible) {
        return;
      }

      const labelsColor = new Color(params.labelsNoseColor);

      const anchorRight = _getObjectByName(
        this._components.nose3D?.holder3D,
        "Anchor-Right",
      );

      const anchorLeft = _getObjectByName(
        this._components.nose3D?.holder3D,
        "Anchor-Left",
      );

      const intersections = [anchorRight, anchorLeft].map((anchor, idx) => {
        const bbox = computeBoundingBoxMorphedHelper(anchor as Mesh);

        return _findPosAndVectorOnShell({
          obj3D: singleGeometryMesh,
          label: params.labelVesselName,
          fromV3: new Vector3(
            bbox ? bbox.centroid.x : 0,
            bbox ? bbox.centroid.y / 3 : 0,
            idx === 1 ? OUT_VEC : -OUT_VEC,
          ),
        });
      });

      (intersections.filter(Boolean) as ILabelInfo[]).forEach((ints) => {
        const labelLenght = (params.labelVesselName || "").length,
          fontSize = labelLenght - 12 <= 0 ? 18 : 18 - (labelLenght - 12);

        if (!ints.intersection) {
          console.log("No intersection found", ints.label);
          return;
        }

        const texture = createLabelTexture({
          text: ints.label,
          width: 256,
          height: 64,
          fontSize,
          flipY: false,
          bold: true,
        });

        const size = new Vector3(4, 1, 4)
          .multiplyScalar(3 + morphConstant * 10)
          .multiplyScalar(0.01);

        const pos = ints.intersection?.point;

        if (!pos) {
          return;
        }

        const orientation = new Euler();
        orientation.y = Math.PI;
        orientation[ints.startBoardSide ? "x" : "z"] = Math.PI;

        const geom = new DecalGeometry(
          ints.intersection.object as Mesh,
          pos,
          orientation,
          size,
        );

        const material = new MeshStandardMaterial({
          color: labelsColor,
          map: texture,
          normalScale: new Vector2(1, 1),
          transparent: true,
          depthTest: true,
          depthWrite: true,
          polygonOffset: true,
          polygonOffsetFactor: -4,
          wireframe: false,
          side: FrontSide,
          envMap: VesselOne._envMap,
          envMapIntensity: 0.95,
        });

        const mesh = new Mesh(geom, material);

        material.needsUpdate = true;
        mesh.name = ints.label;

        labels3D.add(mesh);
      });
    };

    const addBrandTail = (singleGeometryMesh: Mesh) => {
      const labelsColor = new Color(params.labelsTailColor);
      const factorBelow = 0.135 * this.tallScale;

      const tail3DObj = _getObjectByName(
        this._components.tail3D?.holder3D,
        "Tail-Obj",
      );

      if (!tail3DObj) return;

      const tail3DObjWorldPos = new Vector3();
      tail3DObj.getWorldPosition(tail3DObjWorldPos);
      tail3DObj.visible = false;

      /** Create a Texture to put on Tail
       * @param label Label to put
       * @param intInfo Intersection info
       * @param w Width
       * @param h Height
       * @param fs Font size
       * @param bs Box size
       * @param ic Intersection constant
       */
      function createTailTexture(
        label: string,
        intInfo: ILabelInfo,
        w: number,
        h: number,
        fs: number,
        bs: number,
        ic: number,
      ) {
        if (!intInfo.intersection) {
          return;
        }

        const labelSize = (label || "").length,
          fontSize = labelSize - 16 <= 0 ? fs : fs - (labelSize - 16);

        const texture = createLabelTexture({
          text: label,
          width: w,
          height: h,
          fontSize,
          flipY: true,
          bold: true,
        });

        const size = new Vector3(4, 1, 4)
          .multiplyScalar(bs + morphConstant * ic)
          .multiplyScalar(0.01);

        const pos = intInfo.intersection && intInfo.intersection.point;

        if (!pos) {
          return;
        }

        const orientation = new Euler();
        orientation.y = Math.PI / 2;

        const geom = new DecalGeometry(
          singleGeometryMesh,
          pos,
          orientation,
          size,
        );

        const material = new MeshStandardMaterial({
          color: labelsColor,
          map: texture,
          normalScale: new Vector2(1, 1),
          transparent: true,
          depthTest: true,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -4,
          wireframe: false,
          side: FrontSide,
          envMap: VesselOne._envMap,
          envMapIntensity: 0.95,
        });

        const mesh = new Mesh(geom, material);

        material.needsUpdate = true;
        mesh.name = intInfo.label;

        labels3D.add(mesh);
      }

      const bbox = computeBoundingBoxMorphedHelper(tail3DObj as Mesh);
      if (!bbox) return;

      const factorLabelsMissing = {
        0:
          (!params.labelCountry ? 1.125 : 1) *
          (!params.labelIMOCode ? 1.125 : 1),
        1: !params.labelIMOCode ? 1.2 : 1,
        2: 1,
      };

      const x = tail3DObjWorldPos.x + OUT_VEC / 2;

      const labelsPos = [
        {
          x,
          y: -bbox.size.y * factorBelow * factorLabelsMissing[0],
          z: 0,
          label: params.labelVesselName || "",
          w: 256,
          h: 64,
          fs: 23,
          bs: 6,
          ic: 30,
        },
        {
          x,
          y: -bbox.size.y * factorBelow * 1.33 * factorLabelsMissing[1],
          z: 0,
          label: params.labelCountry || "",
          w: 128,
          h: 32,
          fs: 12,
          bs: 3,
          ic: 10,
        },
        {
          x,
          y: -bbox.size.y * factorBelow * 1.55 * factorLabelsMissing[2],
          z: 0,
          label: params.labelIMOCode || "",
          w: 128,
          h: 32,
          fs: 10,
          bs: 3,
          ic: 10,
        },
      ];

      const infos = labelsPos.map((lp) =>
        _findPosAndVectorOnShell(
          {
            obj3D: singleGeometryMesh,
            label: lp.label,
            fromV3: new Vector3(lp.x, lp.y, lp.z),
            dir: new Vector3(-1, 0, 0),
          }, // From behind
        ),
      );

      (infos.filter(Boolean) as ILabelInfo[]).forEach((r, idx) => {
        const lp = labelsPos[idx];
        createTailTexture(r.label, r, lp.w, lp.h, lp.fs, lp.bs, lp.ic);
      });
    };

    const addBrandSides = (singleGeometryMesh: Mesh) => {
      function createSideDecal(info: ILabelInfo) {
        const texture = createLabelTexture({
          text: info.label,
          width: 1024,
          height: 256,
          fontSize: 80,
          flipY: true,
          bold: true,
        });

        const size = new Vector3(4, 1, 0.01)
          .multiplyScalar(14 + morphConstant * 90)
          .multiplyScalar(0.01);

        const pos = info.intersection?.point;

        if (!pos || !info.intersection?.object) {
          return;
        }

        const orientation = new Euler();
        orientation.y = (pos.z < 0 ? -1 : 0) * Math.PI;

        const geom = new DecalGeometry(
          singleGeometryMesh,
          pos,
          orientation,
          size,
        );

        const labelsColor = new Color(params.labelsSideColor);

        const material = new MeshStandardMaterial({
          color: labelsColor,
          map: texture,
          normalScale: new Vector2(1, 1),
          transparent: true,
          depthTest: true,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -1,
          wireframe: false,
          side: FrontSide,
          envMap: VesselOne._envMap,
          envMapIntensity: 0.95,
        });

        const mesh = new Mesh(geom, material);

        material.needsUpdate = true;
        mesh.name = info.label;

        labels3D.add(mesh);
      }

      const modules3D = this._components.body?.modules3D.holder3D;
      if (!modules3D) return;

      const firstModule = modules3D.getObjectByName("Module-Lower") as
        | Mesh
        | undefined;

      if (!firstModule) {
        return;
      }

      const bboxSGM = computeBoundingBoxMorphedHelper(singleGeometryMesh);
      const bboxFM = computeBoundingBoxMorphedHelper(firstModule);

      if (!bboxSGM || !bboxFM) return;

      const res = [1, -1].map((v) =>
        _findPosAndVectorOnShell({
          obj3D: singleGeometryMesh,
          label: params.labelLateral || "",
          fromV3: new Vector3(
            bboxSGM.centroid.x,
            bboxFM.centroid.y * 0.5,
            v * OUT_VEC,
          ),
        }),
      );

      (res.filter(Boolean) as ILabelInfo[]).forEach(createSideDecal);
    };

    const prevShellVisible = this.shellVisible;
    this.shellVisible = true;

    if (!this._singleGeometryMesh) {
      return;
    }

    const singleGeometryMesh = this._singleGeometryMesh;
    singleGeometryMesh.visible = true;

    if (params.bayNumbersVisible) {
      addBayLabels(singleGeometryMesh);
    }

    addBrandNose(singleGeometryMesh);
    addBrandTail(singleGeometryMesh);
    addBrandSides(singleGeometryMesh);

    singleGeometryMesh.visible = false;
    this.shellVisible = prevShellVisible;
  }

  async createSingleGeometry(parentToAddTo: Object3D) {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        this.___createShadowSingleGeometry(parentToAddTo);
        resolve();
      }, 100);
    });
  }

  private ___createShadowSingleGeometry(parentToAddTo: Object3D) {
    //2. Initialize
    const singleGeometry = new BufferGeometry();

    let sumPosCursor = 0,
      sumNormCursor = 0,
      sumUvCursor = 0,
      b;

    const addIf = (node: Mesh | undefined) =>
      node &&
      node.geometry &&
      node.visible &&
      node.name &&
      node.name.indexOf("Module-Separator-Lower") < 0 &&
      node.name.indexOf("Module-Separator-Upper") < 0 &&
      node.name.indexOf("Module-Floor") < 0;

    //3. Calculate Array sizes for each attribute
    this._scene3d.traverse((node) => {
      if (addIf(node as Mesh)) {
        sumPosCursor += (node as Mesh).geometry.getAttribute("position").array
          .length;
        sumNormCursor += (node as Mesh).geometry.getAttribute("normal").array
          .length;
        sumUvCursor += (node as Mesh).geometry.getAttribute("uv").array.length;
      }
    });

    const sumPosArr = new Float32Array(sumPosCursor),
      sumNormArr = new Float32Array(sumNormCursor),
      sumUvArr = new Float32Array(sumUvCursor);

    sumPosCursor = 0;
    sumNormCursor = 0;
    sumUvCursor = 0;

    //4. Add geometries
    this._scene3d.traverse((node) => {
      if (addIf(node as Mesh)) {
        const atts = computeMorphedAttributes(node as Mesh);
        const positionAttribute = atts.morphedPositionAttribute || atts.positionAttribute;
        if (!positionAttribute) {
          console.warn(`No position array for node: ${node.name}`);
          return;
        }

        const posAttArr = positionAttribute.array;
        if (!posAttArr) {
          console.warn(`Invalid position array for node: ${node.name} (undefined)`);
          return;
        }

        const NaNsCount = posAttArr.filter((p: number) => isNaN(p) || Number.isNaN(p)).length
        if (NaNsCount > 0) {
          console.warn(`Invalid position array for node: ${node.name}: ${roundDec(NaNsCount / posAttArr.length * 100, 2)}%`);
          return;
        }

        const ng = (node as Mesh).geometry.clone();

        ng.setAttribute(
          "position",
          atts.morphedPositionAttribute || atts.positionAttribute,
        );
        ng.setAttribute(
          "normal",
          atts.morphedNormalAttribute || atts.normalAttribute,
        );

        node.updateMatrixWorld();
        ng.applyMatrix4(node.matrixWorld);

        for (b = 0; b < posAttArr.length; b++) {
          sumPosArr[b + sumPosCursor] = posAttArr[b];
        }

        sumPosCursor += posAttArr.length;

        const numAttArr = ng.getAttribute("normal").array;

        for (b = 0; b < numAttArr.length; b++) {
          sumNormArr[b + sumNormCursor] = numAttArr[b];
        }

        sumNormCursor += numAttArr.length;

        const uvAttArr = ng.getAttribute("uv").array;

        for (b = 0; b < uvAttArr.length; b++) {
          sumUvArr[b + sumUvCursor] = uvAttArr[b];
        }

        sumUvCursor += uvAttArr.length;
      }
    });

    //6. Update singleGeometry and create Mesh
    singleGeometry.setAttribute("position", new BufferAttribute(sumPosArr, 3));
    singleGeometry.setAttribute("normal", new BufferAttribute(sumNormArr, 3));
    singleGeometry.setAttribute("uv", new BufferAttribute(sumUvArr, 2));

    const singleGeometryMesh = new Mesh(
      singleGeometry,
      new MeshStandardMaterial({ color: 0xaaaaaa, side: DoubleSide }),
    );
    singleGeometryMesh.visible = false;
    singleGeometryMesh.name = "SingleGeometryMesh";

    parentToAddTo.add(singleGeometryMesh);
    this._singleGeometryMesh = singleGeometryMesh;
  }

  replaceHatchCovers(
    coords: { x: number; y: number; move?: boolean }[],
    depth: number,
  ) {
    this.deckVisible = false;
    if (coords.length === 0) {
      console.warn("No hatch covers to replace");
      return undefined;
    }

    try {
      const holder3D = this._components.body?.extras.holder3D;
      if (!holder3D) return;

      const prevDeckMesh = holder3D.getObjectByName("DeckMesh");
      if (prevDeckMesh) {
        disposeObject3D(prevDeckMesh, VesselOne.__getSharedResources());
        prevDeckMesh.removeFromParent();
      }

      const THICKNESS = 0.02;
      const DEPTH = depth;
      const fullCords = coords.slice();

      const lastB = this._parts[this._parts.length - 1];
      const lastXSize = lastB.positionX + lastB.len / 100;
      const lastXCoords = coords[coords.length - 1].x;

      if (lastXSize > lastXCoords) {
        fullCords.push({ x: lastXSize, y: coords[coords.length - 1].y });
      }

      const deckMesh = new DeckMesh(
        fullCords,
        new MeshStandardMaterial({
          color: 0x556270,
          side: DoubleSide,
          envMap: VesselOne._envMap,
        }),
        THICKNESS,
        DEPTH,
      );

      deckMesh.name = "DeckMesh";
      holder3D.add(deckMesh);
      return deckMesh;
    } catch (e) {
      console.error("Error creating deck mesh", e);
      return undefined;
    }
  }
}

function createLabelTexture({
  text,
  width,
  height,
  fontSize,
  flipY,
  bold,
  fillStyle = "#ffffff",
}: {
  text: string;
  width: number;
  height: number;
  fontSize: number;
  flipY: boolean;
  bold: boolean;
  fillStyle?: string;
}) {
  const canvas = document.createElement("canvas"),
    ctx = canvas.getContext("2d"),
    scale = Math.round(window.devicePixelRatio);

  if (!ctx) return;

  canvas.width = width * scale;
  canvas.height = height * scale;
  ctx.scale(scale, scale);

  // ctx.fillStyle = "#222222";
  // ctx.fillRect(0, 0, width, height);

  ctx.font = `${bold ? "bold " : ""}${fontSize}px sans-serif`;
  ctx.fillStyle = fillStyle;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, width / 2, height / 2);

  const texture = new Texture(canvas);
  texture.flipY = flipY;
  texture.needsUpdate = true;

  return texture;
}

/** Finds child by name from Base and applies a function if present
 * @param b Base object
 * @param name Name of the object to find
 * @param cb Callback function to apply to the object
 */
function _getObjectByName(
  b: Mesh | Object3D | undefined,
  name: string,
  cb?: (o: Object3D) => void,
) {
  if (!b) return undefined;
  const o = b.getObjectByName(name);
  if (o && cb) cb(o);
  return o;
}

function setObjectVisibility(
  obj: (string | Object3D | undefined)[],
  base: Object3D,
  visible: boolean,
) {
  obj.forEach((sel) => {
    if (sel === undefined) return;
    if (typeof sel === "string") {
      _getObjectByName(base, sel, (o) => {
        o.visible = visible;
      });
    } else {
      sel.visible = visible;
    }
  });
}

type ILabelInfo = {
  label: string;
  intersection: Intersection | undefined;
  orientation: Euler;
  startBoardSide: 0 | 1 | undefined;
};

class DeckMesh extends Mesh {
  constructor(
    points: { x: number; y: number }[],
    material: Material,
    thickness: number,
    zWidth: number,
  ) {
    super();

    if (points.length < 2) {
      console.warn("DeckMesh: At least two segments are required");
      return;
    }

    this.material = material;
    this.generateGeometry(points, zWidth, thickness);
  }

  private generateGeometry(
    points: { x: number; y: number }[],
    zWidth: number,
    thickness: number,
  ) {
    const geoms: BufferGeometry[] = [];

    let prevX = 0,
      prevY = 0;

    points.forEach((seg, idx) => {
      if (idx === 0) {
        prevX = seg.x;
        prevY = seg.y;
        return;
      }

      // Vertical box
      if (prevY != seg.y) {
        const boxGeom = new BoxGeometry(
          thickness,
          Math.abs(seg.y - prevY) + thickness,
          zWidth,
        );
        boxGeom.translate(prevX, prevY + (seg.y - prevY) / 2, 0);
        geoms.push(boxGeom);
        prevY = seg.y;
      }

      // Horizontal box
      const boxGeom = new BoxGeometry(seg.x - prevX, thickness, zWidth);
      boxGeom.translate(prevX + (seg.x - prevX) / 2, seg.y, 0);
      geoms.push(boxGeom);

      prevX = seg.x;
    });

    const mergedGeometries = mergeGeometries(geoms);
    mergedGeometries.translate(thickness, 0, 0);
    geoms.forEach((g) => g.dispose());

    this.geometry = mergedGeometries;
  }
}
